// src/components/TripsGraph.jsx
import React, { useEffect, useRef, useState } from "react";

// Dark colors for each trip type
const COLORS = {
  OHM_A2C: "#0369a1",
  SUB_A2C: "#0e7490",
  UBER_A2C: "#075985",
  OHM_C2C: "#14532d",
  SUB_C2C: "#166534",
  UBER_C2C: "#15803d",
  OHM_C2A: "#7f1d1d",
  SUB_C2A: "#991b1b",
  UBER_C2A: "#b91c1c",
  B2B: "#78350f",
};

// Legend labels
const LegendLabelMap = {
  OHM_A2C: "OHM A2C",
  SUB_A2C: "Sub A2C",
  UBER_A2C: "Uber A2C",
  OHM_C2C: "OHM C2C",
  SUB_C2C: "Sub C2C",
  UBER_C2C: "Uber C2C",
  OHM_C2A: "OHM C2A",
  SUB_C2A: "Sub C2A",
  UBER_C2A: "Uber C2A",
  B2B: "B2B",
};

export default function TripsGraph({ misData, autoDelay = 15000 }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  // Prepare all trip data
  const allTripData = misData?.info
    ? [
        {
          name: "Hyderabad",
          OHM_A2C: misData?.info?.hyd_ohm?.trips_a2c?.total || 0,
          SUB_A2C: misData?.info?.hyd_subs?.trips_a2c?.total || 0,
          UBER_A2C: misData?.info?.hyd_uber?.trips_a2c?.total || 0,
          OHM_C2C: misData?.info?.hyd_ohm?.trips_c2c?.total || 0,
          SUB_C2C: misData?.info?.hyd_subs?.trips_c2c?.total || 0,
          UBER_C2C: misData?.info?.hyd_uber?.trips_c2c?.total || 0,
          OHM_C2A: misData?.info?.hyd_ohm?.trips_c2a?.total || 0,
          SUB_C2A: misData?.info?.hyd_subs?.trips_c2a?.total || 0,
          UBER_C2A: misData?.info?.hyd_uber?.trips_c2a?.total || 0,
          B2B: misData?.info?.hyd_b2b?.trips?.total || 0,
        },
        {
          name: "Bangalore",
          OHM_A2C: misData?.info?.bgl_ohm?.trips_a2c?.total || 0,
          SUB_A2C: misData?.info?.bgl_subs?.trips_a2c?.total || 0,
          UBER_A2C: misData?.info?.bgl_uber?.trips_a2c?.total || 0,
          OHM_C2C: misData?.info?.bgl_ohm?.trips_c2a_and_c2c?.total || 0,
          SUB_C2C: misData?.info?.bgl_subs?.trips_c2a_and_c2c?.total || 0,
          UBER_C2C: misData?.info?.bgl_uber?.trips_c2a_and_c2c?.total || 0,
          OHM_C2A: 0,
          SUB_C2A: 0,
          UBER_C2A: 0,
          B2B: 0,
        },
        {
          name: "Mangalore",
          OHM_A2C: misData?.info?.mgl_ohm?.trips_a2c?.total || 0,
          SUB_A2C: misData?.info?.mgl_subs?.trips_a2c?.total || 0,
          UBER_A2C: misData?.info?.mgl_uber?.trips_a2c?.total || 0,
          OHM_C2C: misData?.info?.mgl_ohm?.trips_c2a_and_c2c?.total || 0,
          SUB_C2C: misData?.info?.mgl_subs?.trips_c2a_and_c2c?.total || 0,
          UBER_C2C: misData?.info?.mgl_uber?.trips_c2a_and_c2c?.total || 0,
          OHM_C2A: 0,
          SUB_C2A: 0,
          UBER_C2A: 0,
          B2B: 0,
        },
      ]
    : [];

  const cityCount = allTripData.length;

  // Auto rotate
  useEffect(() => {
    if (!cityCount) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % cityCount);
    }, autoDelay);

    return () => clearInterval(intervalRef.current);
  }, [cityCount, autoDelay]);

  if (!misData?.info || cityCount === 0) return null;

  const city = allTripData[currentIndex];
  const totalTrips = Object.keys(city)
    .filter((key) => key !== "name")
    .reduce((sum, key) => sum + city[key], 0);

  const handleDotClick = (idx) => {
    setCurrentIndex(idx);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % cityCount);
    }, autoDelay);
  };

  const TripItem = ({ label, value, color }) =>
    value > 0 ? (
      <div className="flex items-center text-sm mr-4 text-gray-900 whitespace-nowrap">
        <span
          className="w-3 h-3 rounded-full mr-2"
          style={{ backgroundColor: color }}
        />
        <span>{label}:</span>
        <span className="ml-1 font-medium">{value}</span>
      </div>
    ) : null;

  return (
    <div className="flex flex-col items-center">
      <div className="bg-white rounded p-2 border border-gray-200 w-full max-w-full overflow-x-auto">
        <h2 className="text-lg text-center font-medium text-green-800">
          {city.name} Trips
        </h2>
        <p className="font-semibold mb-2 text-gray-900">Total: {totalTrips}</p>

        <div className="flex space-x-4 min-w-max">
          {Object.keys(city)
            .filter((key) => key !== "name")
            .map((key) => (
              <TripItem
                key={key}
                label={LegendLabelMap[key]}
                value={city[key]}
                color={COLORS[key]}
              />
            ))}
        </div>
        <div className="flex mt-4 justify-center space-x-2">
          {allTripData.map((_, idx) => (
            <button
              key={idx}
              onClick={() => handleDotClick(idx)}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === currentIndex ? "bg-blue-600 w-4 h-4" : "bg-gray-400"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Dots */}
    </div>
  );
}
